/**
 * PIN IT EDITIONS (job #941): loading an edition's words and putting them in front of the game's text.
 *
 * Each edition is a JSON file beside this one (hin.json, spa.json, ...), its own lazy chunk, holding the same sections
 * as the English source (ops/pin-editions/source.json): `game` (gameDefaults ids), `names` (the game's languages by
 * key), `here` (the "Other languages spoken here" names, keyed by their English), `tells`, `where` and `note`.
 * Model-drafted and cross-checked by a second model family (ruling r-2026-10-10-translations-word-lists-need-no-native).
 *
 * The edition goes in liveText.ts's edition layer, which gt(), tellFor() and placeFor() read before Popty's live text
 * and the bundled English. Popty's text is English-only in practice (it fills every gap from English), so in an
 * edition it is never asked for anything but the floor under an id the edition lacks.
 */
import type { PinGameId } from './ids'

export * from './ids'

export interface EditionText {
  game: Partial<Record<PinGameId, string>>
  names: Record<string, string>
  here: Record<string, string>
  tells: Record<string, string>
  where: Record<string, string>
  note: Record<string, string>
  /** Each English example line a clip carries, in the edition's language (ops/pin-editions/examples). */
  examples?: Record<string, string>
}

const FILES = import.meta.glob<{ default: EditionText }>('./[a-z][a-z][a-z].json')
const cache = new Map<string, EditionText>()

/** The edition's words, fetched once; null for a code with no file. */
export async function loadEditionText(code: string): Promise<EditionText | null> {
  const hit = cache.get(code)
  if (hit) return hit
  const load = FILES[`./${code}.json`]
  if (!load) return null
  const text = (await load()).default
  cache.set(code, text)
  return text
}

/** The words already fetched for a code, or null: a view reads this synchronously after the route has loaded them. */
export const editionTextNow = (code: string): EditionText | null => cache.get(code) ?? null

/**
 * Pin it's end link. An edition's player is a speaker of that language, so it goes to the English course for them
 * (`edition_learn`); everyone else is offered the course of a language they pinned well (`first_learn`), or nothing.
 */
export function pinEndLink(
  edition: { course: string } | null,
  featured: { course: string; name: string } | null,
  say: { edition: string; featured: (language: string) => string },
): { href: string; text: string } | null {
  if (edition) return { href: `/?course=${encodeURIComponent(edition.course)}`, text: say.edition }
  return featured ? { href: `/?course=${encodeURIComponent(featured.course)}`, text: say.featured(featured.name) } : null
}
